import logoBlack from "@/assets/logo_mark_black.svg";
import logoWhite from "@/assets/logo_mark_white.svg";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import type {
  ItemHistoryActor,
  ItemHistoryActorTone,
} from "./itemHistoryLabels";

const TONE_CLASSES: Record<ItemHistoryActorTone, string> = {
  user: "bg-primary/10 text-primary",
  agent: "bg-info/15 text-info",
  machine: "bg-muted text-muted-foreground",
  draft: "bg-warning/20 text-warning-foreground",
  external: "bg-info/15 text-info-foreground",
};
const AVATAR_CLASSES = "h-[22px] w-[22px]";

/**
 * The actor's avatar, and for an agent write made for a user, the user's
 * avatar tucked behind it. Fills the History line's 34px avatar column.
 * @param actor - Who made the write
 */
export const ItemHistoryAvatars = ({ actor }: { actor: ItemHistoryActor }) => (
  <div className="relative h-[22px] w-[34px]">
    {actor.behalf ? (
      <Avatar className={cn(AVATAR_CLASSES, "absolute left-3 top-0")}>
        {actor.behalf.imageUrl ? (
          <AvatarImage src={actor.behalf.imageUrl} alt="" />
        ) : null}
        {/* One initial, centred in the part the front avatar leaves visible. */}
        <AvatarFallback
          className={cn("pl-3 text-[10px] font-semibold", TONE_CLASSES.user)}
        >
          {actor.behalf.initials.charAt(0)}
        </AvatarFallback>
      </Avatar>
    ) : null}
    <Avatar
      className={cn(
        AVATAR_CLASSES,
        "absolute left-0 top-0",
        // Opaque under its tint, so the avatar behind does not show through.
        actor.behalf && "bg-background ring-2 ring-background",
      )}
    >
      <ActorFace actor={actor} />
    </Avatar>
  </div>
);

// The agent's mark, a member's photo, initials, or the actor's icon.
const ActorFace = ({ actor }: { actor: ItemHistoryActor }) => {
  const Icon = actor.icon;
  const tone = cn("text-[10px] font-semibold", TONE_CLASSES[actor.tone]);
  if (actor.tone === "agent") {
    return (
      <AvatarFallback className={tone}>
        <img src={logoBlack} alt="" className="h-3 w-3 dark:hidden" />
        <img src={logoWhite} alt="" className="hidden h-3 w-3 dark:block" />
      </AvatarFallback>
    );
  }
  return (
    <>
      {actor.imageUrl ? <AvatarImage src={actor.imageUrl} alt="" /> : null}
      <AvatarFallback className={tone}>
        {actor.initials ?? <Icon className="h-3 w-3" aria-hidden />}
      </AvatarFallback>
    </>
  );
};
